import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { errorMessage, unwrap } from 'api/call'
import {
  deleteCustomBannersById,
  getCustomBanners,
  getCustomBannersAll,
  getCustomBannersById,
  postCustomBanners,
  putCustomBannersById,
  type CustomBanner,
  type CustomBannerInput,
  type CustomBannersIndexResponse,
} from 'api/generated'
import { toast } from 'sonner'

interface CustomBannersState {
  loading: boolean
  error: string | null
  banner: CustomBanner | null
  /** Current page of the paginated list (CustomBannersPage). */
  banners: CustomBanner[] | null
  /** Every custom banner, for the positioned banner pickers. */
  allBanners: CustomBanner[] | null
  page: number
  totalPages: number
}

const initialState: CustomBannersState = {
  loading: false,
  error: null,
  banner: null,
  banners: null,
  allBanners: null,
  page: 1,
  totalPages: 0,
}

export const getAllCustomBanners = createAsyncThunk<
  CustomBanner[],
  void,
  { rejectValue: string }
>('customBanners/getAllCustomBanners', async (_, { rejectWithValue }) => {
  try {
    return await unwrap(getCustomBannersAll())
  } catch (error) {
    return rejectWithValue(
      errorMessage(error, 'Failed to fetch all custom banners.')
    )
  }
})

export const save = createAsyncThunk<
  CustomBanner,
  CustomBannerInput,
  { rejectValue: string }
>('customBanners/save', async (body, { rejectWithValue }) => {
  try {
    const response = await unwrap(postCustomBanners({ body }))
    // unwrap throws on `success: false`, so this is the success branch
    if (!('custom_banner' in response)) throw new Error()
    toast.success('Banner inserito con successo')
    return response.custom_banner
  } catch (error) {
    toast.error("Impossibile eseguire l'operazione.")
    return rejectWithValue(errorMessage(error, 'Failed to save custom banner.'))
  }
})

export const fetchCustomBanners = createAsyncThunk<
  CustomBannersIndexResponse,
  number,
  { rejectValue: string }
>('customBanners/fetchCustomBanners', async (page, { rejectWithValue }) => {
  try {
    return await unwrap(getCustomBanners({ query: { page } }))
  } catch (error) {
    return rejectWithValue(
      errorMessage(error, 'Failed to fetch custom banners.')
    )
  }
})

export const deleteBanner = createAsyncThunk<
  CustomBanner,
  number,
  { rejectValue: string }
>('customBanners/deleteBanner', async (id, { rejectWithValue }) => {
  try {
    const { custom_banner } = await unwrap(
      deleteCustomBannersById({ path: { id } })
    )
    toast.success('Banner eliminato con successo')
    return custom_banner
  } catch (error) {
    toast.error("Impossibile eseguire l'operazione.")
    return rejectWithValue(
      errorMessage(error, 'Failed to delete custom banner.')
    )
  }
})

export const fetchBanner = createAsyncThunk<
  CustomBanner,
  number,
  { rejectValue: string }
>('customBanners/fetchBanner', async (id, { rejectWithValue }) => {
  try {
    return await unwrap(getCustomBannersById({ path: { id } }))
  } catch (error) {
    return rejectWithValue(
      errorMessage(error, 'Failed to fetch custom banner.')
    )
  }
})

export const updateBanner = createAsyncThunk<
  void,
  { id: number; data: CustomBannerInput },
  { rejectValue: string }
>('customBanners/updateBanner', async ({ id, data }, { rejectWithValue }) => {
  try {
    await unwrap(putCustomBannersById({ path: { id }, body: data }))
    toast.success('Banner aggiornato con successo')
    window.location.href = '/custom-banner'
  } catch (error) {
    toast.error("Impossibile eseguire l'operazione.")
    return rejectWithValue(
      errorMessage(error, 'Failed to update custom banner.')
    )
  }
})

const customBannersSlice = createSlice({
  name: 'customBanners',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllCustomBanners.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAllCustomBanners.fulfilled, (state, action) => {
        state.loading = false
        state.allBanners = action.payload
      })
      .addCase(getAllCustomBanners.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to fetch all custom banners.'
      })
      .addCase(save.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(save.fulfilled, (state, action) => {
        state.loading = false
        state.banners = [action.payload, ...(state.banners ?? [])]
      })
      .addCase(save.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to save custom banner.'
      })
      .addCase(fetchCustomBanners.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCustomBanners.fulfilled, (state, action) => {
        state.loading = false
        state.totalPages = action.payload.total_pages
        state.page = action.payload.page
        state.banners = action.payload.data
      })
      .addCase(fetchCustomBanners.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to fetch custom banners.'
      })
      .addCase(deleteBanner.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.loading = false
        if (state.banners) {
          state.banners = state.banners.filter(
            (banner) => banner.id !== action.meta.arg
          )
        }
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to delete custom banner.'
      })
      .addCase(fetchBanner.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBanner.fulfilled, (state, action) => {
        state.loading = false
        state.banner = action.payload
      })
      .addCase(fetchBanner.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to fetch custom banner.'
      })
      .addCase(updateBanner.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateBanner.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(updateBanner.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to update custom banner.'
      })
  },
})

export const customBannersReducer = customBannersSlice.reducer
